import React, { useContext, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import config from '../config';
import MirakelParticles from '../components/MirakelParticles';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { setToken, setPermissions } = useContext(AuthContext);

  const handleLogin = async () => {
    setError('');
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${config.API_URL}/auth/login`, {
        email,
        password,
      });
      const token = response.data.token;
      const userPermissions = response.data.permissions;

      localStorage.setItem('token', token);
      localStorage.setItem('permissions', JSON.stringify(userPermissions));
      setToken(token);
      setPermissions(userPermissions);
      navigate('/dashboard');
    } catch (err: unknown) {
      const message =
        axios.isAxiosError(err) &&
        typeof err.response?.data?.message === 'string'
          ? err.response.data.message
          : 'Login failed';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleLogin();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#F7F9FC',
        backgroundImage:
          'linear-gradient(135deg, rgba(37, 99, 235, 0.16) 0%, rgba(6, 182, 212, 0.12) 42%, rgba(249, 115, 22, 0.10) 100%)',
        px: 2,
      }}
    >
      <MirakelParticles
        variant="logo"
        density="hero"
        text="MiRAKEL"
        opacity={0.82}
        sx={{ zIndex: 0 }}
      />
      <Container
        maxWidth="sm"
        sx={{ py: { xs: 5, md: 7 }, position: 'relative', zIndex: 1 }}
      >
        <Box
          sx={{
            width: '100%',
            borderRadius: 2,
            backgroundColor: alpha('#FFFFFF', 0.94),
            border: '1px solid rgba(226, 232, 240, 0.9)',
            boxShadow: '0 18px 50px rgba(15, 23, 42, 0.12)',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              height: 6,
              background:
                'linear-gradient(90deg, #2563EB 0%, #06B6D4 52%, #F97316 100%)',
            }}
          />
          <Box sx={{ p: { xs: 3, sm: 5 } }}>
            <Stack spacing={1.5} sx={{ mb: 4 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'primary.main',
                  color: 'common.white',
                  fontWeight: 800,
                  boxShadow: 'inset 0 -12px 20px rgba(6, 182, 212, 0.45)',
                }}
              >
                M
              </Box>
              <Box>
                <Typography variant="h3" component="h1">
                  MiRAKEL
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  Logga in för att fortsätta
                </Typography>
              </Box>
            </Stack>

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2.5}>
                <TextField
                  fullWidth
                  label="E-post"
                  type="email"
                  variant="outlined"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="email"
                />
                <TextField
                  fullWidth
                  label="Lösenord"
                  type="password"
                  variant="outlined"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                />
                {error && <Alert severity="error">{error}</Alert>}
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={isSubmitting}
                  sx={{ py: 1.35 }}
                >
                  {isSubmitting ? 'Loggar in...' : 'Logga in'}
                </Button>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginPage;
